import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getCIRClients, getCIRSubClients, getCIRCCs, getCIRPOs, getCIRReport, getCIRInvoiceItems,
} from '../../api/ReportsAPI/clientInvoiceReportAPI';

export const SELECT_ALL = 'Select All';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || (typeof e === 'string' ? e : fallback);

// The lookup endpoints return their rows in Data even when IsSuccessful is false (e.g. GetCCbyClient),
// so read Data and ignore the flag.
export const fetchCIRClients = createAsyncThunk('clientInvoiceReport/fetchClients', async (_, { rejectWithValue }) => {
    try { const res = await getCIRClients(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch clients')); }
});

export const fetchCIRSubClients = createAsyncThunk('clientInvoiceReport/fetchSubClients', async (clientCode, { rejectWithValue }) => {
    try { const res = await getCIRSubClients(clientCode); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub-clients')); }
});

export const fetchCIRCCList = createAsyncThunk('clientInvoiceReport/fetchCCList', async ({ clientCode, subClientCode, ccStatus }, { rejectWithValue }) => {
    try { const res = await getCIRCCs(clientCode, subClientCode, ccStatus); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centres')); }
});

export const fetchCIRPOList = createAsyncThunk('clientInvoiceReport/fetchPOList', async ({ clientCode, subClientCode, ccCode }, { rejectWithValue }) => {
    try { const res = await getCIRPOs(clientCode, subClientCode, ccCode); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch PO numbers')); }
});

export const fetchCIRReport = createAsyncThunk('clientInvoiceReport/fetchReport', async (params, { rejectWithValue }) => {
    try { const res = await getCIRReport(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch client invoice report')); }
});

// Items are cached per invoice+PO so re-expanding a row doesn't refetch.
export const itemsKey = (invoiceNo, poNo) => `${invoiceNo}|${poNo}`;

export const fetchCIRInvoiceItems = createAsyncThunk('clientInvoiceReport/fetchInvoiceItems', async ({ invoiceNo, poNo, moid }, { rejectWithValue }) => {
    try {
        const res = await getCIRInvoiceItems(invoiceNo, poNo, moid);
        return res?.Data?.ClientInvoiceItemsDataList || [];
    } catch (e) { return rejectWithValue(err(e, 'Failed to fetch invoice items')); }
});

const initialState = {
    clientList: [], clientListLoading: false,
    subClientList: [], subClientListLoading: false,
    ccList: [], ccListLoading: false,
    poList: [], poListLoading: false,
    report: [], reportLoading: false, reportError: null,
    items: {}, itemsLoading: {}, itemsError: {},
};

const clientInvoiceReportSlice = createSlice({
    name: 'clientInvoiceReport',
    initialState,
    reducers: {
        clearCIRSubClients: (s) => { s.subClientList = []; },
        clearCIRCCList: (s) => { s.ccList = []; },
        clearCIRPOList: (s) => { s.poList = []; },
        clearCIRReport: (s) => { s.report = []; s.reportError = null; s.items = {}; s.itemsLoading = {}; s.itemsError = {}; },
        resetClientInvoiceReport: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCIRClients.pending, (s) => { s.clientListLoading = true; })
            .addCase(fetchCIRClients.fulfilled, (s, a) => { s.clientListLoading = false; s.clientList = a.payload; })
            .addCase(fetchCIRClients.rejected, (s) => { s.clientListLoading = false; s.clientList = []; });

        builder
            .addCase(fetchCIRSubClients.pending, (s) => { s.subClientListLoading = true; })
            .addCase(fetchCIRSubClients.fulfilled, (s, a) => { s.subClientListLoading = false; s.subClientList = a.payload; })
            .addCase(fetchCIRSubClients.rejected, (s) => { s.subClientListLoading = false; s.subClientList = []; });

        builder
            .addCase(fetchCIRCCList.pending, (s) => { s.ccListLoading = true; })
            .addCase(fetchCIRCCList.fulfilled, (s, a) => { s.ccListLoading = false; s.ccList = a.payload; })
            .addCase(fetchCIRCCList.rejected, (s) => { s.ccListLoading = false; s.ccList = []; });

        builder
            .addCase(fetchCIRPOList.pending, (s) => { s.poListLoading = true; })
            .addCase(fetchCIRPOList.fulfilled, (s, a) => { s.poListLoading = false; s.poList = a.payload; })
            .addCase(fetchCIRPOList.rejected, (s) => { s.poListLoading = false; s.poList = []; });

        builder
            .addCase(fetchCIRReport.pending, (s) => { s.reportLoading = true; s.reportError = null; s.items = {}; s.itemsLoading = {}; s.itemsError = {}; })
            .addCase(fetchCIRReport.fulfilled, (s, a) => { s.reportLoading = false; s.report = a.payload; })
            .addCase(fetchCIRReport.rejected, (s, a) => { s.reportLoading = false; s.reportError = a.payload; s.report = []; });

        builder
            .addCase(fetchCIRInvoiceItems.pending, (s, a) => {
                const k = itemsKey(a.meta.arg.invoiceNo, a.meta.arg.poNo);
                s.itemsLoading[k] = true; delete s.itemsError[k];
            })
            .addCase(fetchCIRInvoiceItems.fulfilled, (s, a) => {
                const k = itemsKey(a.meta.arg.invoiceNo, a.meta.arg.poNo);
                s.itemsLoading[k] = false; s.items[k] = a.payload;
            })
            .addCase(fetchCIRInvoiceItems.rejected, (s, a) => {
                const k = itemsKey(a.meta.arg.invoiceNo, a.meta.arg.poNo);
                s.itemsLoading[k] = false; s.itemsError[k] = a.payload;
            });
    },
});

export const {
    clearCIRSubClients, clearCIRCCList, clearCIRPOList, clearCIRReport, resetClientInvoiceReport,
} = clientInvoiceReportSlice.actions;

export const selectCIRClientList          = (s) => s.clientInvoiceReport.clientList;
export const selectCIRClientListLoading   = (s) => s.clientInvoiceReport.clientListLoading;
export const selectCIRSubClientList       = (s) => s.clientInvoiceReport.subClientList;
export const selectCIRSubClientListLoading = (s) => s.clientInvoiceReport.subClientListLoading;
export const selectCIRCCList              = (s) => s.clientInvoiceReport.ccList;
export const selectCIRCCListLoading       = (s) => s.clientInvoiceReport.ccListLoading;
export const selectCIRPOList              = (s) => s.clientInvoiceReport.poList;
export const selectCIRPOListLoading       = (s) => s.clientInvoiceReport.poListLoading;
export const selectCIRReport              = (s) => s.clientInvoiceReport.report;
export const selectCIRReportLoading       = (s) => s.clientInvoiceReport.reportLoading;
export const selectCIRReportError         = (s) => s.clientInvoiceReport.reportError;
export const selectCIRItems               = (s) => s.clientInvoiceReport.items;
export const selectCIRItemsLoading        = (s) => s.clientInvoiceReport.itemsLoading;
export const selectCIRItemsError          = (s) => s.clientInvoiceReport.itemsError;

export default clientInvoiceReportSlice.reducer;
